import { z } from 'zod'
import type { Json } from './json.js'

/**
 * Arbitrary JSON, as a schema.
 *
 * For the places that genuinely carry "whatever document is there" — a marketing
 * page on its way to the editor, which may not satisfy the contract yet. Typed
 * as `z.ZodType<Json>` so what comes out is the `Json` union rather than `any`,
 * which `cse/no-untyped-fetch-json` exists to keep out of the web app.
 */
export const jsonSchema: z.ZodType<Json> = z.lazy(() =>
  z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.null(),
    z.array(jsonSchema),
    z.record(z.string(), jsonSchema),
  ]),
)

/**
 * A JSON text as {@link Json}.
 *
 * `JSON.parse` is untyped. This is the one place that reads it, and the schema
 * is what makes the result JSON rather than a type written next to the parse.
 */
export function parseJson(text: string): Json {
  // eslint-disable-next-line cse/no-unknown-type -- JSON.parse is untyped; jsonSchema validates it on the next line
  const parsed: unknown = JSON.parse(text)
  return jsonSchema.parse(parsed)
}
